import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Menu } from '@headlessui/react';
import { DocumentTextIcon, UserIcon, ArrowPathIcon, ArrowUturnRightIcon } from "@heroicons/react/24/outline";
import {
  EllipsisVerticalIcon,
  XMarkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CurrencyRupeeIcon,
  ChevronDownIcon,
  CalendarIcon,
  FunnelIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  BanknotesIcon,
  WalletIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRegCopy } from "react-icons/fa";
import { FaRegFilePdf } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

// Static transaction data
const staticTransactions = [];

// Date range options
const dateRangeOptions = ['Today', 'This Week', 'This Month', 'Last Month', 'Custom Range'];
const categoryOptions = ['All', 'Topup', 'Recharge', 'Bill Payment', 'Fund Request', 'Others'];
const statusOptions = ['All', 'Success', 'Failed', 'Pending', 'Initiated'];
const disputeOptions = ['All', 'Dispute', 'Dispute Free'];

const OrderInfo = ({ children }) => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId') || '';
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [cardData, setCardData] = useState([]);
  const [refreshingIds, setRefreshingIds] = useState(new Set());
  const [access_data, setAccess_data] = useState([]);
  const [providers, setProviders] = useState([]);
  const [operatorList, setOperatorList] = useState([]);
  const [operatorSearch, setOperatorSearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // Check session & encryption
  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchReport();
      fetchProviders();
    }
  }, [access_data?.username]);

  useEffect(() => {
    if (filterDrawerOpen) {
      // disable scrolling
      document.body.style.overflow = "hidden";
    } else {
      // enable scrolling again
      document.body.style.overflow = "auto";
    }

    // cleanup when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [filterDrawerOpen]);



  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  const [filters, setFilters] = useState({
    dateRange: 'Today',
    startDate: new Date(),
    endDate: new Date(),
    category: '',
    rrn: '',
    minAmount: '',
    maxAmount: '',
    mobileNumber: '',
    orderId: '',
    UserId: userId,
    ClientOrderId: '',
    provider: '',
    operator: '',
    status: ''
  });

  // Load static data initially
  useEffect(() => {
    setLoading(true);
    setTransactions(staticTransactions);
    setFilteredTransactions(staticTransactions);
    setPagination(prev => ({
      ...prev,
      totalItems: staticTransactions.length,
      totalPages: Math.ceil(staticTransactions.length / prev.itemsPerPage)
    }));
    setLoading(false);
  }, []);

  // Apply filters to API data
  const applyFilters = () => {
    fetchReport();
    setFilterDrawerOpen(false);
  };

  const resetFilters = () => {
    setFilters({
      dateRange: 'Today',
      startDate: new Date(),
      endDate: new Date(),
      category: '',
      rrn: '',
      minAmount: '',
      maxAmount: '',
      mobileNumber: '',
      orderId: '',
      UserId: '',
      ClientOrderId: '',
      provider: '',
      operator: '',
      status: ''
    });
    setOperatorSearch('');
    setProviderSearch('');
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateRangeChange = (range) => {
    const today = new Date();
    let startDate, endDate = today;

    switch (range) {
      case 'Today':
        startDate = new Date(today);
        break;
      case 'This Week':
        startDate = new Date(today.setDate(today.getDate() - today.getDay()));
        break;
      case 'This Month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'Last Month':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      default:
        startDate = filters.startDate;
        endDate = filters.endDate;
    }

    setFilters(prev => ({
      ...prev,
      dateRange: range,
      startDate,
      endDate
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  // Calculate paginated data
  const getPaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const endIndex = startIndex + pagination.itemsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const fetchReport = async () => {
    setLoading(true);

    try {
      // Format dates for API
      const formatDate = (date) => {
        return date ? new Date(date).toISOString().split('T')[0] : '';
      };

      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          date_range: filters.dateRange,
          from_date: formatDate(filters.startDate),
          to_date: formatDate(filters.endDate),
          category: filters.category,
          rrn: filters.rrn,
          min_amount: filters.minAmount,
          max_amount: filters.maxAmount,
          mobile_number: filters.mobileNumber,
          order_id: filters.orderId,
          user_id: filters.UserId,
          client_order_id: filters.ClientOrderId,
          provider_id: filters.provider,
          operator_id: filters.operator,
          status: filters.status === 'Success' ? '1' :
            filters.status === 'Failed' ? '2' :
              filters.status === 'Pending' ? '3' :
                filters.status === 'Initiated' ? '0' : '',
        }
      };

      console.log("Request Payload:", dataToEncrypt);

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}order-list.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setCardData(result.dashboard || []);

        const apiTransactions = result.data.map(item => ({
          input_params: item.number,
          id: item.order_id,
          amount: `₹${parseFloat(item.amount).toFixed(2)}`,
          user_profit: `₹${parseFloat(item.user_profit).toFixed(2)}`,
          admin_profit: `₹${parseFloat(item.user_profit).toFixed(2)}`,
          status: item.status,
          date: item.createDate,
          time: item.createAt,
          category: item.biller_name,
          rrn: item.transaction_id,
          mobileNumber: item.number || '',
          UserId: item.user_id || '',
          provider: item.provider_id || '',
          operator: item.operator_id || '',
          user_name: item.user_name || '',
          name: item.name,
          company_name: item.company_name
        }));

        setTransactions(apiTransactions);
        setFilteredTransactions(apiTransactions);

        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: apiTransactions.length,
          totalPages: Math.ceil(apiTransactions.length / prev.itemsPerPage)
        }));
      } else {
        toast.error(result.message || "Failed to fetch report data.");
        setTransactions([]);
        setFilteredTransactions([]);
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: 0,
          totalPages: 1
        }));
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("API Error:", error);
      setTransactions([]);
      setFilteredTransactions([]);
      setPagination(prev => ({
        ...prev,
        currentPage: 1,
        totalItems: 0,
        totalPages: 1
      }));
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'transactional-provider'
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}provider-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setProviders(result.data || []);
      } else {
        throw new Error(result.message || "Failed to fetch operators");
      }
    } catch (error) {
      toast.error(error.message || "Error fetching operators");
    }
  };

  const fetchOperators = async (searchTerm = '') => {
    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          operator_name: searchTerm
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}get-operator-search.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setOperatorList(result.data || []);
      }
    } catch (error) {
      toast.error("Error fetching operators");
      console.error("Operator fetch error:", error);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (operatorSearch.length > 2 || operatorSearch.length === 0) {
        fetchOperators(operatorSearch);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [operatorSearch]);

  const handleAction = (action, order_id, User_id) => {
    if (action === 'Order Info') {
      window.open(`/order-details?orderId=${order_id}`, '_blank');
    }
    else if (action === 'User Info') {
      window.open(`/profile?userId=${User_id}`, '_blank');
    }
    else if (action === 'Retrigger Webhook') {
      Swal.fire({
        title: 'Are you sure?',
        text: "Retrigger webhook or fetch status!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            Swal.fire({
              title: 'Processing...',
              html: 'Please wait while we process your request',
              allowOutsideClick: false,
              allowEscapeKey: false,
              allowEnterKey: false,
              showConfirmButton: false,
              willOpen: () => {
                Swal.showLoading();
              }
            });

            const dataToEncrypt = {
              user_id: access_data?.username || '',
              login_id: access_data?.login_id || '',
              device_id: getDeviceInfo().deviceId,
              payload: {
                order_id: order_id
              }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
              toast.error("Encryption failed. Please try again.");
              setLoading(false);
              return;
            }

            const payloadToSend = {
              encrypted_key: encryptedMessage.encryptedKey,
              encrypted_data: encryptedMessage.encryptedData,
            };

            const response = await fetch(`${API_URL}order-retrigger.php`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
              },
              body: JSON.stringify(payloadToSend),
            });

            const result = await response.json();

            if (result.status === true) {
              toast.success("Webhook retriggered successfully!");
            } else {
              toast.error(result.message || "Failed to retrigger webhook.");
            }
          } catch (error) {
            toast.error("An unexpected error occurred while retriggering webhook. Please try again.");
          } finally {
            Swal.close();
          }
        }
      });
    }
  }

  const handleRecheck = async (orderId) => {
    setRefreshingIds(prev => {
      const newSet = new Set(prev);
      newSet.add(orderId);
      return newSet;
    });

    try {

      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          order_id: orderId
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}order-recheck.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        setTransactions(prev => prev.map(txn =>
          txn.id === orderId ? { ...txn, status: result.newStatus } : txn
        ));
        toast.success("Status refreshed");
      } else {
        toast.error(result.message || "Failed to retrigger webhook.");
      }
    } catch (error) {
      toast.error("Failed to refresh status");
    } finally {
      setRefreshingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(label + " copied successfully");
  }

  // Filter providers based on search
  const filteredProviders = providers.filter(provider =>
    provider.name.toLowerCase().includes(providerSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 overflow-auto">
          {/* Filter Drawer */}
          <AnimatePresence>
            {filterDrawerOpen && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'tween' }}
                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
              >
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium">Filter Order</h2>
                    <button
                      onClick={() => setFilterDrawerOpen(false)}
                      className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    {/* Date Range */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Date Range</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.dateRange}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {dateRangeOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleDateRangeChange(option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                      {filters.dateRange === 'Custom Range' && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <div>
                            <DatePicker
                              selected={filters.startDate}
                              onChange={(date) => handleFilterChange('startDate', date)}
                              selectsStart
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              popperPlacement="bottom-start"
                              popperClassName="z-50" // ensures above drawer
                              popperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      boundary: "viewport", // keeps inside screen
                                    },
                                  },
                                ],
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                          <div>
                            <DatePicker
                              selected={filters.endDate}
                              onChange={(date) => handleFilterChange('endDate', date)}
                              selectsEnd
                              startDate={filters.startDate}
                              endDate={filters.endDate}
                              minDate={filters.startDate}
                              popperPlacement="bottom-end"
                              popperClassName="z-50"
                              popperProps={{
                                modifiers: [
                                  {
                                    name: "preventOverflow",
                                    options: {
                                      boundary: "viewport",
                                    },
                                  },
                                ],
                              }}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Category */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Category</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.category || 'All'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {categoryOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('category', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>

                    {/* Status */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>{filters.status || 'All'}</span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none">
                          {statusOptions.map((option) => (
                            <Menu.Item key={option}>
                              {({ active }) => (
                                <button
                                  onClick={() => handleFilterChange('status', option)}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {option}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>

                    {/* Provider */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Provider</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>
                            {filters.provider
                              ? providers.find(p => p.id === filters.provider)?.name || 'Select Provider'
                              : 'All Providers'}
                          </span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                          <div className="sticky top-0 bg-white dark:bg-gray-700 px-2 py-1">
                            <div className="relative">
                              <input
                                type="text"
                                value={providerSearch}
                                onChange={(e) => setProviderSearch(e.target.value)}
                                className="w-full pl-8 pr-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                                placeholder="Search provider..."
                              />
                              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => {
                                  handleFilterChange('provider', '');
                                  setProviderSearch('');
                                }}
                                className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                              >
                                All Providers
                              </button>
                            )}
                          </Menu.Item>
                          {filteredProviders.map((provider) => (
                            <Menu.Item key={provider.id}>
                              {({ active }) => (
                                <button
                                  onClick={() => {
                                    handleFilterChange('provider', provider.id);
                                    setProviderSearch('');
                                  }}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {provider.name}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>

                    {/* Operator */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Operator</label>
                      <Menu as="div" className="relative">
                        <Menu.Button className="w-full flex justify-between items-center px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-left text-sm focus:outline-none">
                          <span>
                            {filters.operator
                              ? operatorList.find(o => o.operator_id === filters.operator)?.biller_name || 'Select Operator'
                              : 'All Operators'}
                          </span>
                          <ChevronDownIcon className="h-4 w-4" />
                        </Menu.Button>
                        <Menu.Items className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none max-h-60 overflow-auto">
                          <div className="sticky top-0 bg-white dark:bg-gray-700 px-2 py-1">
                            <div className="relative">
                              <input
                                type="text"
                                value={operatorSearch}
                                onChange={(e) => setOperatorSearch(e.target.value)}
                                className="w-full pl-8 pr-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700"
                                placeholder="Search operator..."
                              />
                              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            </div>
                          </div>
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                onClick={() => {
                                  handleFilterChange('operator', '');
                                  // Don't clear the search term here
                                }}
                                className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                              >
                                All Operators
                              </button>
                            )}
                          </Menu.Item>
                          {operatorList.map((operator) => (
                            <Menu.Item key={operator.operator_id}>
                              {({ active }) => (
                                <button
                                  onClick={() => {
                                    handleFilterChange('operator', operator.operator_id);
                                    // Don't clear the search term here to keep it visible
                                  }}
                                  className={`${active ? 'bg-gray-100 dark:bg-gray-600' : ''} block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200`}
                                >
                                  {operator.biller_name}
                                </button>
                              )}
                            </Menu.Item>
                          ))}
                        </Menu.Items>
                      </Menu>
                    </div>

                    {/* RRN */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">RRN/TXN</label>
                      <input
                        type="text"
                        value={filters.rrn}
                        onChange={(e) => handleFilterChange('rrn', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter RRN"
                      />
                    </div>

                    {/* Amount Range */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Amount Range</label>
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          type="number"
                          value={filters.minAmount}
                          onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                          placeholder="Min"
                        />
                        <input
                          type="number"
                          value={filters.maxAmount}
                          onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                          placeholder="Max"
                        />
                      </div>
                    </div>

                    {/* Mobile Number */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Mobile Number</label>
                      <input
                        type="text"
                        value={filters.mobileNumber}
                        onChange={(e) => handleFilterChange('mobileNumber', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter Mobile"
                      />
                    </div>

                    {/* Order ID */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Order ID</label>
                      <input
                        type="text"
                        value={filters.orderId}
                        onChange={(e) => handleFilterChange('orderId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter Order ID"
                      />
                    </div>

                    {/* User ID */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User ID</label>
                      <input
                        type="text"
                        value={filters.UserId}
                        onChange={(e) => handleFilterChange('UserId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter User ID"
                      />
                    </div>

                    {/* Client Order ID */}
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client Order ID</label>
                      <input
                        type="text"
                        value={filters.ClientOrderId}
                        onChange={(e) => handleFilterChange('ClientOrderId', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700"
                        placeholder="Enter Client Order ID"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={resetFilters}
                      className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                    >
                      Reset
                    </button>
                    <button
                      onClick={applyFilters}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Overlay when filter drawer is open */}
          {filterDrawerOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setFilterDrawerOpen(false)}
            />
          )}

          <main className="flex-1 p-6 overflow-auto">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-1" />
                  Back
                </button>
                <h2 className="text-xl font-semibold">
                  Order Management
                </h2>
                <button
                  onClick={() => setFilterDrawerOpen(true)}
                  className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                >
                  <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </button>
              </div>
              {children || (
                <>
                  {/* Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {cardData.map((card, index) => (
                      <div key={index} className={`${card.color} rounded-lg shadow p-6 text-white`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium opacity-80">{card.title}</p>
                            <h3 className="text-2xl font-bold mt-1">₹{card.amount}</h3>
                          </div>
                          <div className="p-2 rounded-full bg-white bg-opacity-20">
                            <CurrencyRupeeIcon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div className="mt-4 flex items-center">
                          <span className={`ml-1 text-sm font-medium`}>
                            {card.count}  Transaction count
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Transaction Cards */}
                  {/* Transaction Cards */}
                  <div className="mb-6">
                    {loading && (
                      <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                      </div>
                    )}

                    {!loading && (
                      <>
                        {getPaginatedData().length > 0 ? (
                          <div className="overflow-x-auto rounded-lg shadow">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600 text-sm">
                              <thead className="bg-gray-100 dark:bg-gray-700">
                                <tr>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    Order ID
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    User
                                  </th>
                                  <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    Amount
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    Biller
                                  </th>
                                  <th className="px-3 py-2 text-center font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    Status
                                  </th>
                                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    TXN/UTR
                                  </th>
                                  <th className="px-3 py-2 text-center font-semibold text-gray-700 dark:text-gray-300 text-sm">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {getPaginatedData().map((txn, i) => (
                                  <tr
                                    key={i}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                                  >
                                    {/* Order ID */}
                                    <td className="px-3 py-2 align-middle">
                                      <div className="flex items-center gap-1">
                                        <span className="truncate max-w-[100px]">{txn.id}</span>
                                        <button
                                          onClick={() => copyText(txn.id, "Order ID")}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                          title="Copy Order ID"
                                        >
                                          <FaRegCopy className="h-4 w-4" />
                                        </button>
                                      </div>
                                      <div className="text-xs text-gray-400">{txn.time}</div>
                                    </td>

                                    {/* User */}
                                    <td className="px-3 py-2 align-middle">
                                      <div className="truncate max-w-[120px]">{txn.name}</div>
                                      <div className="flex items-center gap-1 text-xs text-gray-400">
                                        <span className="truncate max-w-[80px]">{txn.UserId}</span>
                                        <button
                                          onClick={() => copyText(txn.UserId, "User ID")}
                                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                          title="Copy User ID"
                                        >
                                          <FaRegCopy className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </td>

                                    {/* Amount */}
                                    <td className="px-3 py-2 align-middle text-right">
                                      {txn.amount}
                                      <div className="flex justify-end gap-2 text-xs mt-0.5">
                                        {txn.user_profit && (
                                          <span className="text-green-600 dark:text-green-400">
                                            U:{txn.user_profit}
                                          </span>
                                        )}
                                        {txn.admin_profit && (
                                          <span className="text-blue-600 dark:text-blue-400">
                                            A:{txn.admin_profit}
                                          </span>
                                        )}
                                      </div>
                                    </td>

                                    {/* Biller */}
                                    <td className="px-3 py-2 align-middle">
                                      <div className="flex items-center gap-2">
                                        {/* Operator Icon */}
                                        <div className="flex-shrink-0 flex items-center justify-center">
                                          <img
                                            src={txn.operator_icon_url}
                                            alt={txn.company_name}
                                            className="w-6 h-6 rounded-full object-cover"
                                          />
                                        </div>

                                        {/* Biller Details */}
                                        <div className="flex flex-col">
                                          <span className="truncate font-medium text-blue-600 dark:text-blue-400 max-w-[140px]">
                                            {txn.category}
                                          </span>
                                          <div className="flex items-center gap-1 text-xs text-gray-400">
                                            <span className="truncate max-w-[100px]">{txn.mobileNumber}</span>
                                            {txn.mobileNumber && (
                                              <button
                                                onClick={() => copyText(txn.mobileNumber, "Mobile number")}
                                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                                title="Copy Mobile Number"
                                              >
                                                <FaRegCopy className="h-4 w-4" />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Status */}
                                    <td className="px-3 py-2 align-middle text-center">
                                      <div className="flex flex-row items-center gap-1">
                                        <span
                                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${txn.status === "1"
                                            ? "bg-green-700 text-white dark:bg-green-800 dark:text-green-200"
                                            : txn.status === "0"
                                              ? "bg-yellow-700 text-white dark:bg-yellow-800 dark:text-yellow-200"
                                              : txn.status === "2"
                                                ? "bg-red-700 text-white dark:bg-red-800 dark:text-red-200"
                                                : txn.status === "3"
                                                  ? "bg-blue-700 text-white dark:bg-blue-800 dark:text-blue-200"
                                                  : "bg-gray-700 text-white dark:bg-gray-700 dark:text-gray-300"
                                            }`}
                                        >
                                          {txn.status === "1"
                                            ? "Success"
                                            : txn.status === "0"
                                              ? "Initiated"
                                              : txn.status === "2"
                                                ? "Failed"
                                                : "Pending"}
                                        </span>

                                        <button
                                          onClick={() => handleRecheck(txn.id)}
                                          className={`p-1 rounded-full text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 ${refreshingIds.has(txn.id) ? "animate-spin" : ""
                                            }`}
                                          title="Refresh status"
                                          disabled={refreshingIds.has(txn.id)}
                                        >
                                          <ArrowPathIcon className="h-5 w-5" />
                                        </button>
                                      </div>
                                    </td>


                                    {/* TXN/UTR */}
                                    <td className="px-3 py-2 align-middle">
                                      <div className="flex flex-col">
                                        <span className="truncate font-bold text-green-500 max-w-[120px]">{txn.company_name}</span>
                                        <div className="flex items-center gap-1">
                                          <span className="truncate max-w-[100px]">{txn.rrn}</span>
                                          {txn.rrn.trim() && (
                                            <button
                                              onClick={() => copyText(txn.rrn, "Transaction ID")}
                                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                              title="Copy Transaction ID"
                                            >
                                              <FaRegCopy className="h-4 w-4" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </td>

                                    {/* Actions */}
                                    <td className="px-3 py-2 align-middle text-center">
                                      <div className="flex justify-center gap-2">
                                        <button
                                          onClick={() => handleAction("Order Info", txn.id, txn.UserId)}
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="Order Info"
                                        >
                                          <DocumentTextIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                        <button
                                          onClick={() => handleAction("User Info", txn.id, txn.UserId)}
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="User Info"
                                        >
                                          <UserIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleAction("Retrigger Webhook", txn.id, txn.UserId)
                                          }
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="Retrigger Webhook"
                                        >
                                          <ArrowUturnRightIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                        <a
                                          target='_blank'
                                          href={`https://server.1recharge.org/invoice.php?order_id=${txn.id}`}
                                          className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
                                          title="Retrigger Webhook"
                                        >
                                          <FaRegFilePdf className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </a>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="p-4 text-center text-sm text-gray-500">
                            No transactions found
                          </div>
                        )}



                        {/* Pagination - Optimized for mobile */}
                        <div className="flex items-center justify-between px-2 sm:px-4 py-3 border-t border-gray-200 dark:border-gray-700 mt-4">
                          <div className="flex-1 flex justify-between sm:hidden">
                            <button
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                              className="relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                              className="ml-2 relative inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600"
                            >
                              Next
                            </button>
                          </div>
                          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                                Showing <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> to{' '}
                                <span className="font-medium">
                                  {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                                </span>{' '}
                                of <span className="font-medium">{pagination.totalItems}</span> results
                              </p>
                            </div>
                            <div>
                              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                <button
                                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                                  disabled={pagination.currentPage === 1}
                                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                >
                                  <span className="sr-only">Previous</span>
                                  <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                                </button>

                                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                  let pageNum;
                                  if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (pagination.currentPage <= 3) {
                                    pageNum = i + 1;
                                  } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                  } else {
                                    pageNum = pagination.currentPage - 2 + i;
                                  }

                                  return (
                                    <button
                                      key={pageNum}
                                      onClick={() => handlePageChange(pageNum)}
                                      className={`relative inline-flex items-center px-3 py-1 sm:px-4 sm:py-2 border text-xs sm:text-sm font-medium ${pagination.currentPage === pageNum
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-gray-600 dark:text-white'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300'
                                        }`}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}

                                <button
                                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                                  disabled={pagination.currentPage === pagination.totalPages}
                                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-xs sm:text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                                >
                                  <span className="sr-only">Next</span>
                                  <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
                                </button>
                              </nav>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </main>
        </main>
      </div>
      <ToastContainer />
    </div>
  );
};

export default OrderInfo;